import React from "react";
import {
  Activity, ClipboardList, ShieldCheck, BarChart3,
  Phone, MapPin, Clock, Globe, Sparkles, Layers,
  Stethoscope, TrendingUp, Users, ShieldCheck as ShieldCheck2, Bot,
} from "lucide-react";

/**
 * Healthcare-flavored sidebar for the public chatbot.
 *
 * Consultative Experience v1 — Claude-level layout for healthcare tenants.
 *
 * Sections (top → bottom):
 *
 *   1. **Our platform** — data-driven cards from `chatbot.platform_modules`.
 *      Each module: {icon, title, sub}. Icon keys map to lucide icons below.
 *      Falls back to feature-flag cards when modules empty.
 *
 *   2. **Common questions** — clickable text rows from `suggested_prompts`.
 *      Same `onAsk` flow as the existing pill row.
 *
 *   3. **Talk to our team** — contact card.
 *
 *   4. **Trust strip** — small credentials chips at the very bottom.
 */

// Icon key → lucide component (for data-driven platform modules).
const ICON_MAP = {
  "stethoscope": Stethoscope,
  "trending-up": TrendingUp,
  "sparkles": Sparkles,
  "users": Users,
  "shield-check": ShieldCheck2,
  "bot": Bot,
  "activity": Activity,
  "clipboard": ClipboardList,
  "bar-chart": BarChart3,
  "layers": Layers,
};

// Legacy fallback for tenants that haven't yet configured platform_modules.
const HEALTHCARE_FLAG_CARDS = {
  pricing_modal: {
    Icon: BarChart3,
    h: "Pricing & estimates",
    sub: "Tailored quote for your practice size and specialty.",
  },
  rcm_intake_modal: {
    Icon: ClipboardList,
    h: "Revenue cycle & billing",
    sub: "Claims, denials, AR health — discussed in plain language.",
  },
  procare_it: {
    Icon: ShieldCheck,
    h: "IT & cybersecurity",
    sub: "Practice-grade IT and security, optional bundle.",
  },
  competitor_intelligence: {
    Icon: Layers,
    h: "Compare platforms",
    sub: "Side-by-side with the platforms you're evaluating.",
  },
};

export default function HealthcareSidebar({
  open,
  company,
  chatbot,
  featureFlags,
  suggestedPrompts,
  onAsk,
  onOpenLead,
  onClose,
}) {
  const c = company || {};
  const bot = chatbot || {};
  const flags = featureFlags || {};
  const prompts = Array.isArray(suggestedPrompts) ? suggestedPrompts : [];
  const platformModules = Array.isArray(bot.platform_modules) ? bot.platform_modules : [];
  const credentials = Array.isArray(bot.credentials_strip) ? bot.credentials_strip : [];

  // Prefer data-driven modules; fall back to legacy feature-flag cards.
  const useModules = platformModules.length > 0;
  const fallbackCards = Object.entries(HEALTHCARE_FLAG_CARDS).filter(
    ([flag]) => flags[flag]
  );

  return (
    <>
      <aside
        className={`rch-sidebar rch-hc-sidebar ${open ? "open" : ""}`}
        data-testid="rch-sidebar"
      >
        <div className="rch-sidebar-inner">
          {/* OUR PLATFORM — data-driven feature cards */}
          {useModules ? (
            <div className="rch-section rch-hc-section" data-testid="rch-hc-platform-section">
              <h4>Our platform</h4>
              {platformModules.map((m, i) => {
                const Icon = ICON_MAP[m.icon] || Sparkles;
                return (
                  <div
                    className="rch-hc-platform-card rch-hc-module-card"
                    key={`${m.title}-${i}`}
                    data-testid={`rch-hc-module-${(m.title || "").toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  >
                    <div className="rch-hc-platform-ico"><Icon className="w-4 h-4" /></div>
                    <div className="rch-hc-platform-text">
                      <div className="rch-hc-platform-h">{m.title}</div>
                      <div className="rch-hc-platform-sub">{m.sub}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            fallbackCards.length > 0 && (
              <div className="rch-section rch-hc-section" data-testid="rch-hc-platform-section">
                <h4>Platform & services</h4>
                {fallbackCards.map(([flag, { Icon, h, sub }]) => (
                  <div
                    className="rch-hc-platform-card"
                    key={flag}
                    data-testid={`rch-hc-card-${flag}`}
                  >
                    <div className="rch-hc-platform-ico"><Icon className="w-4 h-4" /></div>
                    <div className="rch-hc-platform-text">
                      <div className="rch-hc-platform-h">{h}</div>
                      <div className="rch-hc-platform-sub">{sub}</div>
                    </div>
                  </div>
                ))}
              </div>
            )
          )}

          {prompts.length > 0 && (
            <div className="rch-section rch-hc-section">
              <h4>Common questions</h4>
              {prompts.map(q => (
                <div
                  key={q}
                  className="rch-faq-item rch-hc-faq-item"
                  role="button"
                  tabIndex={0}
                  onClick={() => { onAsk?.(q); onClose?.(); }}
                  onKeyDown={(e) => { if (e.key === "Enter") { onAsk?.(q); onClose?.(); } }}
                  data-testid={`rch-faq-${q.slice(0, 24).replace(/\s+/g, "-").toLowerCase()}`}
                >
                  <span>{q}</span><span aria-hidden>→</span>
                </div>
              ))}
            </div>
          )}

          {(c.address || c.phone || c.business_hours || c.website) && (
            <div className="rch-section rch-hc-section">
              <div className="rch-cta-card rch-hc-cta-card">
                <h5>Talk to our team</h5>
                <p>Share a few details and a specialist will reach out — typically within one business day.</p>
                <div className="rch-cta-contact">
                  {c.phone && (
                    <div><Phone className="inline w-3 h-3 mr-1.5" /><a href={`tel:${c.phone}`}>{c.phone}</a></div>
                  )}
                  {c.address && (
                    <div><MapPin className="inline w-3 h-3 mr-1.5" />{c.address}</div>
                  )}
                  {c.business_hours && (
                    <div><Clock className="inline w-3 h-3 mr-1.5" />{c.business_hours}</div>
                  )}
                  {c.website && (
                    <div><Globe className="inline w-3 h-3 mr-1.5" /><a href={c.website} target="_blank" rel="noreferrer">{c.website.replace(/^https?:\/\//, "")}</a></div>
                  )}
                </div>
                <button
                  className="rch-cta-btn rch-hc-cta-btn"
                  onClick={() => { onOpenLead?.(); onClose?.(); }}
                  data-testid="rch-sidebar-contact"
                >
                  <Sparkles className="w-3.5 h-3.5 mr-1.5 inline" />
                  Contact our team
                </button>
              </div>
            </div>
          )}

          {/* Trust strip — credentials chips at the very bottom */}
          {credentials.length > 0 && (
            <div className="rch-section rch-hc-section rch-hc-trust-strip" data-testid="rch-hc-trust-strip">
              {credentials.map((c2) => (
                <span key={c2} className="rch-hc-trust-chip">{c2}</span>
              ))}
            </div>
          )}

          {/* Empty-state safety */}
          {!useModules && fallbackCards.length === 0 && prompts.length === 0
            && !(c.address || c.phone || c.business_hours || c.website) && (
            <div className="rch-section rch-hc-section">
              <p style={{ fontSize: 13, color: "var(--rch-muted)" }}>
                Ask anything about our platform — I'm trained on this practice's content.
              </p>
            </div>
          )}
        </div>
      </aside>
      <div
        className={`rch-backdrop ${open ? "show" : ""}`}
        onClick={onClose}
        data-testid="rch-backdrop"
      />
    </>
  );
}
